import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FaDumbbell, FaAppleAlt, FaVideo, FaBook, FaCalendar, FaComments, FaCog, FaBars, FaTimes, FaStore, FaUtensils, FaEnvelope, FaCrown, FaFire, FaBolt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { getWeeklyBuyersText, getRemainingSpotsText } from '@/lib/socialProof';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = ({ isAuthenticated, user }) => {
  const navigate = useNavigate();
  const { config, loading: configLoading } = useSiteConfig();
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('monthly'); // monthly, bimonthly, quarterly, semiannual, yearly
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [bgImageLoaded, setBgImageLoaded] = useState(false);
  
  // Imagen hero - selección por género del usuario logueado (fallback al genérico)
  // Sin llamadas a IA — solo lógica React.
  const userGender = (user?.gender || '').toLowerCase();
  const genderSpecificHero =
    userGender === 'male' ? config?.hero_image_male :
    userGender === 'female' ? config?.hero_image_female : '';
  const heroImage = configLoading ? null : (
    genderSpecificHero ||
    config?.hero_image ||
    'https://customer-assets.emergentagent.com/job_dashboard-status-fix/artifacts/z1sj60ko_WhatsApp%20Image%202026-02-05%20at%2018.11.50.jpeg'
  );

  // Precargar imagen de fondo del hero
  useEffect(() => {
    if (heroImage) {
      const img = new Image();
      img.onload = () => setBgImageLoaded(true);
      img.onerror = () => setBgImageLoaded(true); // Mostrar aunque falle
      img.src = heroImage;
    }
  }, [heroImage]);

  // Hero visible solo cuando config y background están listos
  const heroReady = !configLoading && (bgImageLoaded || !heroImage);
  
  // Colores del tema desde la configuración - usar colores rosa por defecto (NO rojo)
  const themeColor = config?.theme_color || 'var(--brand-primary)';
  const secondaryColor = config?.secondary_color || '#00F0FF';
  const accentColor = config?.accent_color || 'var(--brand-primary-strong)';
  const gradientStart = config?.gradient_start || themeColor;
  const gradientEnd = config?.gradient_end || accentColor;

  useEffect(() => {
    // Cargar planes de suscripción desde el backend
    fetchSubscriptionPlans();
    
    // Handle session_id from URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSession(sessionId);
    }
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      // Cache-buster: garantiza que el landing muestre la descripción
      // recién editada por el admin sin que quede cacheada por el navegador.
      const response = await fetch(`${BACKEND_URL}/api/public/subscription-plans?_ts=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      const data = await response.json();
      if (data.plans && data.plans.length > 0) {
        setSubscriptionPlans(data.plans);
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
  };

  const processSession = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/session-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        window.location.href = '/onboarding';
      }
    } catch (error) {
      console.error('Session processing failed:', error);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleWhatsApp = () => {
    window.open('https://wa.link/wul3d0', '_blank');
  };

  // Función para cerrar menú móvil con vibración háptica
  const closeMobileMenu = () => {
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
    setMobileMenuOpen(false);
  };

  // Función para scroll suave a una sección sin cambiar la URL
  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // Función para navegación desde menú móvil con vibración
  const handleMobileNav = (path) => {
    if (navigator.vibrate) {
      navigator.vibrate(40);
    }
    setMobileMenuOpen(false);
    if (path.startsWith('#')) {
      scrollToSection(path.substring(1));
    } else {
      navigate(path);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative">
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 50%), radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.1), transparent 40%)'
        }}
      />
      
      {/* ChatBot Widget */}
      {/* ChatWidget removido — se conserva el botón de correo y WhatsApp. */}

      {/* Navbar - Estilo limpio */}
      <nav className="fixed w-full top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl font-bold"
            data-testid="logo"
          >
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
            >
              DeisyCFit
            </span>
          </motion.div>
          
          <div className="flex items-center gap-2 md:gap-4">
            {/* Links de navegación - Solo desktop */}
            <span onClick={() => scrollToSection('servicios')} className="hidden lg:block text-gray-400 hover:text-[var(--theme-color)] transition-colors font-medium text-sm cursor-pointer">Servicios</span>
            <span onClick={() => navigate('/plans')} className="hidden lg:block text-gray-400 hover:text-[var(--theme-color)] transition-colors font-medium text-sm cursor-pointer">Planes</span>
            <span onClick={() => navigate('/videos-organized')} className="hidden md:block text-gray-400 hover:text-[var(--theme-color)] transition-colors font-medium text-sm cursor-pointer">Videos</span>
            <span onClick={() => navigate('/ebooks')} className="hidden md:block text-gray-400 hover:text-[var(--theme-color)] transition-colors font-medium text-sm cursor-pointer">Ebooks</span>
            <span onClick={() => navigate('/recetarios')} className="hidden md:block text-gray-400 hover:text-[var(--theme-color)] transition-colors font-medium text-sm cursor-pointer">Recetarios</span>
            <span onClick={() => navigate('/tienda')} className="hidden md:block text-gray-400 hover:text-[var(--theme-color)] transition-colors font-medium text-sm cursor-pointer">Tienda</span>
            <span onClick={() => scrollToSection('contacto')} className="hidden lg:block text-gray-400 hover:text-[var(--theme-color)] transition-colors font-medium text-sm cursor-pointer">Contacto</span>
            
            {/* Botón Menú Móvil - Solo visible en móvil */}
            <Button 
              onClick={() => {
                // Vibración háptica al abrir el menú
                if (navigator.vibrate) {
                  navigator.vibrate(50);
                }
                setMobileMenuOpen(true);
              }} 
              variant="ghost"
              className="md:hidden text-white hover:bg-white/10 p-2"
              data-testid="mobile-menu-button"
            >
              <FaBars className="text-xl" />
            </Button>
            
            {isAuthenticated ? (
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="text-white font-medium rounded-full px-4 md:px-6 text-xs md:text-sm" 
                style={{ background: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
                data-testid="dashboard-button"
              >
                Dashboard
              </Button>
            ) : (
              <Button 
                onClick={handleLogin} 
                className="text-white font-medium rounded-full px-4 md:px-6 text-xs md:text-sm" 
                style={{ background: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
                data-testid="login-button"
              >
                <span className="hidden sm:inline">Iniciar </span>Sesión
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Menu Drawer - Overlay con fondo oscuro */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop oscuro */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-[100] md:hidden"
              onClick={closeMobileMenu}
            />
            
            {/* Panel del menú */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 right-0 h-full w-[280px] z-[101] md:hidden"
              style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #111 100%)' }}
            >
              {/* Header del menú */}
              <div className="flex items-center justify-between p-5 border-b border-white/10">
                <span 
                  className="text-xl font-bold bg-clip-text text-transparent"
                  style={{ backgroundImage: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
                >
                  Menú
                </span>
                <button 
                  onClick={closeMobileMenu}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <FaTimes className="text-white text-xl" />
                </button>
              </div>
              
              {/* Items del menú */}
              <div className="p-4 space-y-2">
                {/* Servicios */}
                <motion.div
                  onClick={() => handleMobileNav('#servicios')}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-all group"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor}40, ${secondaryColor}40)` }}
                  >
                    <FaDumbbell className="text-[var(--theme-color)]" />
                  </div>
                  <span className="text-white font-medium group-hover:text-[var(--theme-color)] transition-colors">Servicios</span>
                </motion.div>

                {/* Planes */}
                <motion.div
                  onClick={() => handleMobileNav('/plans')}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.12 }}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-all group cursor-pointer"
                  data-testid="mobile-menu-planes"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor}40, ${secondaryColor}40)` }}
                  >
                    <FaCrown className="text-yellow-400" />
                  </div>
                  <span className="text-white font-medium group-hover:text-yellow-400 transition-colors">Planes</span>
                </motion.div>

                {/* Videos */}
                <motion.div
                  onClick={() => handleMobileNav('/videos-organized')}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 }}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-all group cursor-pointer"
                  data-testid="mobile-menu-videos"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor}40, ${secondaryColor}40)` }}
                  >
                    <FaVideo className="text-[var(--secondary-color)]" />
                  </div>
                  <span className="text-white font-medium group-hover:text-[var(--secondary-color)] transition-colors">Videos</span>
                </motion.div>

                {/* Ebooks */}
                <motion.div
                  onClick={() => handleMobileNav('/ebooks')}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-all group cursor-pointer"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor}40, ${secondaryColor}40)` }}
                  >
                    <FaBook className="text-purple-400" />
                  </div>
                  <span className="text-white font-medium group-hover:text-purple-400 transition-colors">Ebooks</span>
                </motion.div>

                {/* Recetarios */}
                <motion.div
                  onClick={() => handleMobileNav('/recetarios')}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 }}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-all group cursor-pointer"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor}40, ${secondaryColor}40)` }}
                  >
                    <FaUtensils className="text-green-400" />
                  </div>
                  <span className="text-white font-medium group-hover:text-green-400 transition-colors">Recetarios</span>
                </motion.div>

                {/* Tienda */}
                <motion.div
                  onClick={() => handleMobileNav('/tienda')}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-all group cursor-pointer"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor}40, ${secondaryColor}40)` }}
                  >
                    <FaStore className="text-yellow-400" />
                  </div>
                  <span className="text-white font-medium group-hover:text-yellow-400 transition-colors">Tienda</span>
                </motion.div>

                {/* Contacto */}
                <motion.div
                  onClick={() => handleMobileNav('#contacto')}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 }}
                  className="flex items-center gap-4 p-4 rounded-xl hover:bg-white/5 transition-all group"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${themeColor}40, ${secondaryColor}40)` }}
                  >
                    <FaEnvelope className="text-red-400" />
                  </div>
                  <span className="text-white font-medium group-hover:text-red-400 transition-colors">Contacto</span>
                </motion.div>
              </div>

              {/* Footer del menú con botones */}
              <div className="absolute bottom-0 left-0 right-0 p-4 pb-20 border-t border-white/10 bg-[#0a0a0a]">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-3"
                >
                  <button 
                    onClick={() => handleMobileNav('/plans')}
                    className="block w-full py-3 text-center font-bold rounded-full text-white"
                    style={{ background: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
                  >
                    VER PLANES
                  </button>
                  <button 
                    onClick={() => { 
                      if (navigator.vibrate) navigator.vibrate(40);
                      handleWhatsApp(); 
                      setMobileMenuOpen(false); 
                    }}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-full transition-colors"
                  >
                    <FaComments /> WhatsApp
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero Section - Full background image style like bootyandlegsacademy */}
      <section 
        className="relative z-10 min-h-[90vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: heroReady ? `url(${heroImage})` : 'none',
          backgroundColor: '#0a0a0a',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: heroReady ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out'
        }}
      >
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-black/70 via-black/40 to-black/20" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-20">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left side - Text content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              {/* Badge */}
              <div 
                className="inline-block px-4 md:px-5 py-2 rounded-full mb-6 backdrop-blur-sm"
                style={{ 
                  background: `linear-gradient(135deg, ${themeColor}90, ${secondaryColor}60)`,
                  boxShadow: `0 0 30px ${themeColor}40`
                }}
              >
                <p className="text-white text-xs md:text-sm font-bold uppercase tracking-[0.15em] md:tracking-[0.2em]">
                  Tu entrenadora personal
                </p>
              </div>

              {/* Main headline */}
              <h1 
                className="text-4xl md:text-5xl lg:text-7xl font-black mb-4 md:mb-6 leading-[0.95] text-white uppercase"
                style={{ fontFamily: 'var(--font-titles, Poppins), sans-serif' }}
                data-testid="hero-title"
              >
                {config?.hero_title_line1 || 'Transforma'}
                <br />
                <span 
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
                >
                  {config?.hero_title_line2 || 'tu cuerpo'}
                </span>
                <br />
                {config?.hero_title_line3 || 'tu vida'}
              </h1>

              {/* Description */}
              <p className="text-gray-300 text-base md:text-lg lg:text-xl mb-6 md:mb-8 max-w-lg leading-relaxed">
                {config?.hero_subtitle || 'Espacio de transformación integral con planes personalizados de entrenamiento y nutrición.'} 
                {config?.hero_highlight && (
                  <> Especialista en <span style={{ color: secondaryColor, fontWeight: 600 }}>{config.hero_highlight}</span>.</>
                )}
                {!config?.hero_highlight && (
                  <> Especialista en <span style={{ color: secondaryColor, fontWeight: 600 }}>salud hormonal femenina</span>.</>
                )}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button 
                  size="lg" 
                  className="text-white px-6 md:px-8 py-4 md:py-5 rounded-full font-bold uppercase tracking-wider transition-all hover:scale-105 text-sm md:text-base w-full sm:w-auto"
                  style={{ 
                    background: `linear-gradient(135deg, ${themeColor}, #ff6b9d)`,
                    boxShadow: `0 0 40px ${themeColor}60`
                  }}
                  onClick={() => window.open('https://wa.link/8wemdy', '_blank')}
                  data-testid="hero-cta-button"
                >
                  Quiero mi transformación
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="px-6 md:px-8 py-4 md:py-5 rounded-full font-bold uppercase tracking-wider transition-all hover:bg-white/10 text-sm md:text-base w-full sm:w-auto"
                  style={{ 
                    borderColor: 'rgba(255,255,255,0.4)', 
                    borderWidth: '2px',
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)'
                  }}
                  onClick={() => navigate('/plans')}
                  data-testid="contact-button"
                >
                  Ver programas
                </Button>
              </div>
            </motion.div>

            {/* Right side - Feature image (visible on tablet and desktop) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden md:flex justify-center items-center mt-8 lg:mt-0"
            >
              <div className="relative">
                {/* Glow effect behind image */}
                <div 
                  className="absolute inset-0 rounded-3xl blur-3xl opacity-50"
                  style={{ background: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})`, transform: 'scale(0.9)' }}
                />
                {/* Main image - clear, not blurred */}
                {/* Placeholder while loading */}
                {(!heroImage || !imageLoaded) && (
                  <div 
                    className="relative w-[300px] md:w-[350px] lg:w-[400px] h-[400px] md:h-[450px] lg:h-[500px] rounded-3xl border-2 border-white/20"
                    style={{ 
                      background: `linear-gradient(135deg, ${themeColor}30, ${secondaryColor}30)`,
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                )}
                {heroImage && (
                  <img 
                    src={heroImage} 
                    alt="Deisy Carolina - Entrenadora Personal"
                    className={`relative w-[300px] md:w-[350px] lg:w-[400px] h-[400px] md:h-[450px] lg:h-[500px] object-cover object-top rounded-3xl shadow-2xl border-2 border-white/20 transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0 absolute'}`}
                    style={{ filter: 'contrast(1.05) saturate(1.1)' }}
                    onLoad={() => setImageLoaded(true)}
                  />
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* +800 Vidas Cambiadas + Transformaciones Section */}
      <section className="relative z-10 py-16 px-6 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          {/* +800 vidas cambiadas badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-3 mb-4">
              <span 
                className="text-5xl md:text-6xl font-black bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
                data-testid="lives-changed-counter"
              >
                +800
              </span>
              <span className="text-xl md:text-2xl font-bold text-white uppercase tracking-wide">
                Vidas Cambiadas
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-white">
              Transformaciones Reales
            </h2>
            <p className="text-gray-500">Resultados comprobados de mis clientas</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              {
                url: 'https://customer-assets.emergentagent.com/job_fitcoach-hub-9/artifacts/8jcnvr0s_WhatsApp%20Image%202025-11-25%20at%206.29.20%20PM.jpeg',
                title: 'Transformación 1'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_fitcoach-hub-9/artifacts/5tlbmv50_WhatsApp%20Image%202025-11-25%20at%206.30.33%20PM.jpeg',
                title: 'Transformación 2'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_fitcoach-hub-9/artifacts/6gkpgu6p_WhatsApp%20Image%202025-11-25%20at%206.30.33%20PMk.jpeg',
                title: 'Transformación 3'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_fitcoach-hub-9/artifacts/e7zbmteo_WhatsApp%20Image%202025-11-25%20at%206.30.33%20PMl.jpeg',
                title: 'Transformación 4'
              },
              {
                url: 'https://customer-assets.emergentagent.com/job_fitcoach-hub-9/artifacts/dy6s5gom_WhatsApp%20Image%202025-11-25%20at%206.31.39%20PM.jpeg',
                title: 'Transformación 5'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group"
              >
                <div className="rounded-2xl overflow-hidden border border-white/10 hover:border-[var(--brand-primary)]/50 transition-all duration-300">
                  <img 
                    src={item.url} 
                    alt={item.title} 
                    className="w-full h-[280px] object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Quote Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center max-w-3xl mx-auto"
          >
            <blockquote className="text-2xl md:text-3xl font-medium text-white italic mb-6">
              "Sé el tipo de persona que hace que otros quieran mejorar."
            </blockquote>
            <p className="text-gray-500">
              Especialista en salud hormonal femenina. Planes personalizados para tus necesidades.
            </p>
            <Button 
              className="mt-6 text-white px-8 py-3 rounded-full font-medium hover:scale-105 transition-transform"
              style={{ background: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
              onClick={() => window.open('https://wa.link/8wemdy', '_blank')}
              data-testid="transformations-cta-button"
            >
              Quiero mi transformación
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Especialidades Section */}
      <section className="relative z-10 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p 
              className="text-xs font-bold uppercase tracking-[0.3em] mb-3"
              style={{ color: secondaryColor }}
            >
              Especialidades
            </p>
            <h2 
              className="text-3xl md:text-4xl font-bold text-white mb-3 uppercase"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Atención Especializada
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Planes personalizados para condiciones específicas de salud
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Metabólicas',
                items: ['Diabetes', 'Resistencia a la Insulina', 'Síndrome Metabólico'],
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                  </svg>
                )
              },
              {
                title: 'Hormonales',
                items: ['SOP', 'Hipotiroidismo', 'Hipertiroidismo'],
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
                  </svg>
                )
              },
              {
                title: 'Cardiovasculares',
                items: ['Hipertensión', 'Problemas Cardíacos', 'Control de Presión'],
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                )
              },
              {
                title: 'Digestiva y Mental',
                items: ['Problemas Digestivos', 'TCA', 'Relación con la Comida'],
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
                  </svg>
                )
              }
            ].map((category, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-[#1a1a1a] rounded-2xl p-6 hover:bg-[#222] transition-all duration-300 border border-white/5"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-white"
                  style={{ background: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
                >
                  {category.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">{category.title}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: secondaryColor }}></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="servicios" className="relative z-10 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div 
                className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
              >
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">Fitness y Entrenamiento</h3>
              <p style={{ color: themeColor }} className="font-medium">+7 años de experiencia</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div 
                className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${secondaryColor}, #CCFF00)` }}
              >
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">Planes Online</h3>
              <p style={{ color: '#00F0FF' }} className="font-medium">+500 clientas transformadas</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div 
                className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #CCFF00, var(--brand-primary))' }}
              >
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">Comunidad</h3>
              <p className="text-[var(--brand-primary)] font-medium">Apoyo y motivación</p>
            </motion.div>
          </div>

          {/* Servicios Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              ¿Qué incluyen mis planes?
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Todo lo que necesitas para alcanzar tus objetivos de salud y bienestar
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Rutinas */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5"
            >
              <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Rutinas Personalizadas</h3>
              <p className="text-gray-500">Planes de entrenamiento adaptados a tu nivel y objetivos específicos</p>
            </motion.div>

            {/* Nutrición */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5"
            >
              <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m18-4.5a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Planes Nutricionales</h3>
              <p className="text-gray-500">Dietas personalizadas según tus necesidades y balance hormonal</p>
            </motion.div>

            {/* Videos */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5"
            >
              <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Videos de Entrenamiento</h3>
              <p className="text-gray-500">Biblioteca completa con tutoriales y ejercicios explicados paso a paso</p>
            </motion.div>

            {/* Ebooks - NUEVO */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              onClick={() => navigate('/ebooks')}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5 cursor-pointer group"
            >
              <div className="flex justify-between items-start mb-5">
                <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                </div>
                <span className="text-xs bg-[var(--brand-primary)] text-white px-2 py-1 rounded-full">Disponible</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Ebooks</h3>
              <p className="text-gray-500 mb-3">Guías completas de nutrición, entrenamiento y bienestar</p>
              <div className="flex items-center gap-2 text-[var(--brand-primary)] text-sm font-medium group-hover:gap-3 transition-all">
                <span>Ver Ebooks</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>

            {/* Recetarios en Video - NUEVO */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              onClick={() => navigate('/recetarios')}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5 cursor-pointer group"
            >
              <div className="flex justify-between items-start mb-5">
                <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                </div>
                <span className="text-xs bg-[var(--brand-primary)] text-white px-2 py-1 rounded-full">Nuevo</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Recetarios en Video</h3>
              <p className="text-gray-500 mb-3">Aprende a preparar recetas saludables paso a paso</p>
              <div className="flex items-center gap-2 text-[var(--brand-primary)] text-sm font-medium group-hover:gap-3 transition-all">
                <span>Ver Recetarios</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>

            {/* Tienda Online - NUEVO */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              onClick={() => navigate('/tienda')}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5 cursor-pointer group"
            >
              <div className="flex justify-between items-start mb-5">
                <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                </div>
                <span className="text-xs bg-[var(--brand-primary)] text-white px-2 py-1 rounded-full">Tienda</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Tienda Online</h3>
              <p className="text-gray-500 mb-3">Suplementos, accesorios, ropa e implementos deportivos</p>
              <div className="flex items-center gap-2 text-[var(--brand-primary)] text-sm font-medium group-hover:gap-3 transition-all">
                <span>Ir a la Tienda</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </motion.div>

            {/* Asesoría */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5"
            >
              <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Asesoría 1 a 1</h3>
              <p className="text-gray-500">Seguimiento personalizado con ajustes en tiempo real según tu progreso</p>
            </motion.div>

            {/* Soporte */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="bg-[#1a1a1a] rounded-2xl p-8 hover:bg-[#222] transition-all duration-300 border border-white/5"
            >
              <div className="w-14 h-14 rounded-xl bg-[var(--brand-primary)]/10 flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Soporte WhatsApp</h3>
              <p className="text-gray-500">Resuelve todas tus dudas al instante con atención personalizada</p>
            </motion.div>
          </div>

          {/* Nota de descuentos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-[#1a1a1a] px-6 py-3 rounded-full border border-white/10">
              <svg className="w-5 h-5 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
              </svg>
              <span className="text-gray-400 text-sm">
                <span className="text-white font-medium">Ebooks:</span> Basic 20% • Premium 30% • Gold 50% | 
                <span className="text-white font-medium"> Tienda:</span> Basic 5% • Premium 10% • Gold 15%
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Plans Preview Section */}
      <section id="planes" className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <p 
              className="text-xs font-bold uppercase tracking-[0.3em] mb-3"
              style={{ color: themeColor }}
            >
              Nuestros planes
            </p>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-white uppercase">
              Elige Tu{' '}
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
              >
                Plan Ideal
              </span>
            </h2>
            <p className="text-gray-500 mb-6">Selecciona el plan que mejor se adapte a ti</p>
            
            {/* Selector de Período - METALIZADO */}
            <div className="inline-flex flex-wrap justify-center gap-1 p-1 rounded-full" style={{ background: 'rgba(255, 255, 255, 0.05)' }}>
              {[
                { id: 'monthly', label: 'Mensual' },
                { id: 'bimonthly', label: 'Bimestre' },
                { id: 'quarterly', label: 'Trimestral' },
                { id: 'semiannual', label: 'Semestral' },
                { id: 'yearly', label: 'Anual' }
              ].map((period) => (
                <button
                  key={period.id}
                  onClick={() => setSelectedPeriod(period.id)}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-all text-white"
                  style={selectedPeriod === period.id 
                    ? { background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }
                    : { background: 'transparent', color: 'rgba(255,255,255,0.6)' }
                  }
                >
                  {period.label}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {(subscriptionPlans.length > 0 ? subscriptionPlans.map(plan => {
              // Obtener el precio según el período seleccionado
              const getPriceForPeriod = () => {
                const periodConfig = {
                  monthly: { price: plan.price_monthly, priceCop: plan.price_monthly_cop, label: '/mes', months: 1 },
                  bimonthly: { price: plan.price_bimonthly, priceCop: plan.price_bimonthly_cop, label: '/2 meses', months: 2 },
                  quarterly: { price: plan.price_quarterly, priceCop: plan.price_quarterly_cop, label: '/3 meses', months: 3 },
                  semiannual: { price: plan.price_semiannual, priceCop: plan.price_semiannual_cop, label: '/6 meses', months: 6 },
                  yearly: { price: plan.price_yearly, priceCop: plan.price_yearly_cop, label: '/año', months: 12 }
                };
                
                // 1. Intentar el período seleccionado
                const config = periodConfig[selectedPeriod];
                if (config.price && config.price > 0) {
                  return { price: config.price, priceCop: config.priceCop || 0, label: config.label, available: true };
                }
                
                // 2. Fallback: buscar CUALQUIER precio disponible en orden
                const fallbackOrder = ['monthly', 'bimonthly', 'quarterly', 'semiannual', 'yearly'];
                for (const period of fallbackOrder) {
                  const fb = periodConfig[period];
                  if (fb.price && fb.price > 0) {
                    return { price: fb.price, priceCop: fb.priceCop || 0, label: fb.label, available: true };
                  }
                }
                
                return { price: 0, priceCop: 0, label: '', available: false };
              };
              
              const priceInfo = getPriceForPeriod();
              
              return {
                id: plan.id,
                name: plan.name,
                subtitle: plan.description || plan.name,
                priceValue: priceInfo.price,
                priceCop: priceInfo.priceCop,
                priceLabel: priceInfo.label,
                priceAvailable: priceInfo.available,
                features: plan.features || [],
                popular: plan.popular,
                slug: plan.slug,
                cover_image: plan.cover_image || plan.background_image || null
              };
            }) : [
              {
                name: 'Fit Evolution',
                subtitle: 'Con Automatización',
                priceValue: 20,
                priceLabel: '/mes',
                priceAvailable: true,
                features: ['Sistema automático', 'Según tus objetivos', 'Planes personalizados', 'Soporte por email']
              },
              {
                name: 'Plan Premium',
                subtitle: 'Asesoría 1-a-1',
                priceValue: 74,
                priceLabel: '/mes',
                priceAvailable: true,
                features: ['100% Personalizado', 'Check-in semanal', 'WhatsApp 24/7', 'Videollamadas', 'Entreno presencial'],
                popular: true
              },
              {
                name: 'Plan Gold',
                subtitle: 'Elite - Todo Incluido',
                priceValue: 175,
                priceLabel: '/mes',
                priceAvailable: true,
                features: ['Todo incluido', '8 entrenos virtuales/mes', 'Videollamada semanal', '15% dto. suplementos', 'Acceso total']
              },
              {
                name: 'Videos',
                subtitle: 'Acceso Mensual',
                priceValue: 7,
                priceLabel: '/mes',
                priceAvailable: true,
                features: ['Acceso 1 mes', 'Videos de entreno', 'Videos de nutrición', 'Actualizaciones incluidas']
              }
            ]).map((plan, idx) => (
              <motion.div
                key={plan.id || idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className={`relative ${plan.popular ? 'lg:-mt-4 lg:mb-4' : ''}`}
              >
                {plan.popular && (
                  <div 
                    className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider"
                    style={{ background: `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})` }}
                  >
                    Popular
                  </div>
                )}
                
                <div 
                  className="h-full rounded-3xl transition-all flex flex-col overflow-hidden"
                  style={{ 
                    background: 'rgba(26, 26, 26, 0.8)',
                    backdropFilter: 'blur(10px)',
                    border: plan.popular ? '2px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {/* Cover Image */}
                  {plan.cover_image && (
                    <div className="w-full h-44 overflow-hidden">
                      <img 
                        src={plan.cover_image} 
                        alt={plan.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="p-6 flex flex-col flex-grow">
                  <p className="text-sm font-medium mb-1 whitespace-pre-line" style={{ color: themeColor }}>{plan.subtitle}</p>
                  <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>{plan.name}</h3>

                  {/* Prueba social + urgencia — sólo en el plan self-serve (Fit Evolution / básico) */}
                  {(() => {
                    const nameLower = String(plan.name || '').toLowerCase();
                    const slugLower = String(plan.slug || '').toLowerCase();
                    const isFitEvo = nameLower.includes('fit evolution') || nameLower.includes('básico') || nameLower.includes('basico') || slugLower.includes('basic') || slugLower.includes('fit-evolution') || plan.id === 'plan_basic';
                    if (!isFitEvo) return null;
                    return (
                      <div className="flex flex-wrap gap-2 mb-3">
                        <div
                          className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={{
                            background: 'rgba(var(--brand-primary-glow), 0.15)',
                            color: 'var(--brand-primary)',
                            border: '1px solid rgba(var(--brand-primary-glow), 0.3)'
                          }}
                          data-testid={`plan-social-proof-${idx}`}
                        >
                          <FaFire className="text-[10px]" />
                          <span>{getWeeklyBuyersText()}</span>
                        </div>
                        <div
                          className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                          style={{
                            background: 'rgba(245, 158, 11, 0.15)',
                            color: '#FBBF24',
                            border: '1px solid rgba(245, 158, 11, 0.3)'
                          }}
                          data-testid={`plan-remaining-spots-${idx}`}
                        >
                          <FaBolt className="text-[10px]" />
                          <span>{getRemainingSpotsText()}</span>
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div className="mb-4">
                    {plan.priceAvailable ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>${(plan.priceCop || 0).toLocaleString('es-CO')}</span>
                          <span className="text-gray-400 text-sm">COP{plan.priceLabel}</span>
                        </div>
                        {plan.priceValue > 0 && (
                          <div className="flex items-baseline gap-1 mt-1">
                            <span className="text-lg font-semibold" style={{ color: '#00F0FF' }}>${plan.priceValue?.toLocaleString()}</span>
                            <span className="text-gray-500 text-xs">USD{plan.priceLabel}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">No disponible</span>
                    )}
                  </div>
                  
                  {/* ALL features shown - no "Ver más" */}
                  <ul className="space-y-2 mb-5 flex-grow">
                    {(plan.features || []).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: themeColor }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className="w-full text-white rounded-full font-semibold transition-all hover:opacity-90 hover:scale-[1.02] py-5"
                    style={{ background: `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})` }}
                    onClick={() => {
                      // slug amigable para URL pública (fit-evolution para plan basic)
                      let friendlySlug = plan.slug || (plan.name || '').toLowerCase().replace(/\s+/g, '-');
                      if (friendlySlug === 'basic' || plan.id === 'plan_basic') friendlySlug = 'fit-evolution';
                      navigate(`/checkout?plan=${friendlySlug}&period=${selectedPeriod}`);
                    }}
                    data-testid={`plan-button-${idx}`}
                  >
                    Elegir Plan
                  </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* This section was moved up, right after the hero */}

      {/* CTA Section - ELECTRIC OBSIDIAN */}
      <section id="contacto" className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center rounded-3xl p-12"
            style={{ 
              background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)',
              boxShadow: '0 0 60px rgba(var(--brand-primary-glow), 0.4)'
            }}
          >
            <p 
              className="text-sm font-bold uppercase tracking-[0.2em] mb-4 text-white/80"
            >
              Tu transformación empieza hoy
            </p>
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4 text-white uppercase"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              ¿Lista para comenzar?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Únete a cientos de mujeres que ya están transformando sus vidas
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-[#0a0a0a] hover:bg-gray-100 font-bold uppercase tracking-wider rounded-full px-10 py-6 transition-all hover:scale-105"
                onClick={() => window.open('https://wa.link/8wemdy', '_blank')}
                data-testid="cta-start-button"
              >
                Quiero mi transformación
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-[#0a0a0a] rounded-full px-10 py-6 font-bold uppercase tracking-wider transition-all hover:scale-105"
                onClick={handleWhatsApp}
                data-testid="cta-whatsapp-button"
              >
                WhatsApp
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer - ELECTRIC OBSIDIAN */}
      <footer className="relative z-10 py-12 px-6" style={{ background: '#050505' }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 
                className="text-2xl font-bold mb-1 bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(135deg, ${themeColor}, ${secondaryColor})` }}
              >
                DeisyCFit
              </h3>
              <p className="text-gray-400 text-sm">Tu aliada en el camino hacia el fitness</p>
            </div>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 hover:text-[var(--brand-primary)] transition-colors">Instagram</a>
              <a href="#" className="text-gray-400 hover:text-[var(--brand-primary)] transition-colors">Facebook</a>
              <a href="#" onClick={handleWhatsApp} className="text-gray-400 hover:text-[var(--brand-primary)] transition-colors cursor-pointer">WhatsApp</a>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center">
            <p className="text-gray-500 text-sm">&copy; 2025 DeisyCFit. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
